import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import type { Task } from '../../lib/tasksApi';
import type { Reminder } from '../../lib/remindersApi';
import type { MonthGridDay } from '../../lib/calendarDates';
import { toDateKey, weekdayLabels } from '../../lib/calendarDates';
import { statusBadgeStyle } from '../tasks/taskConstants';
import { TaskHoverCard } from '../tasks/TaskHoverCard';
import { Avatar } from '../tasks/linear/Avatar';
import { CalendarEventChip, LeaveChip, MeetingChip, ReminderChip } from './chips';
import type { VideoMeetingSummary } from '../../lib/meetApi';
import type { CalendarEvent } from '../../lib/calendarApi';
import type { LeaveDay } from '../../lib/leaveApi';
import { LEAVE_TYPE_COLOR } from '../../lib/leaveConstants';
import { CalendarDnd, DraggableCalendarItem, DroppableCalendarDay } from './calendarDnd';
import { useMoveTaskToDay } from './useMoveTaskToDay';
import { calendarEventColor } from './previewCards';
import { TaskListRow, calendarTaskRow } from '../tasks/TaskListRow';
import { useSidebar } from '../layout/SidebarContext';
import { useI18n } from '../../contexts/I18nContext';

interface MonthGridProps {
  /** 6×7 Monday-first grid from monthGrid() — adjacent-month cells render muted. */
  weeks: MonthGridDay[][];
  /** Tasks bucketed by local YYYY-MM-DD due-date key. */
  tasksByDay: Map<string, Task[]>;
  /** The caller's open reminders bucketed by local YYYY-MM-DD due key. */
  remindersByDay?: Map<string, Reminder[]>;
  /** Firmio Meet meetings bucketed by the local YYYY-MM-DD of their start. */
  meetingsByDay?: Map<string, VideoMeetingSummary[]>;
  /** The selected person's Google Calendar events, bucketed by local day. */
  eventsByDay?: Map<string, CalendarEvent[]>;
  /**
   * Approved leave, already one entry per covered day (the server fans a
   * request out), so a day's bucket is simply everyone away on it.
   */
  leaveByDay?: Map<string, LeaveDay[]>;
  /** Label each chip with its assignee — on when no assignee filter narrows the view. */
  showAssignee?: boolean;
}

const MAX_VISIBLE = 3;
/** Dots shown in a phone-sized cell before they turn into a "+N" counter. */
const MAX_DOTS = 3;

/** The weekday header strip, shared by both layouts. */
function WeekdayHeader({ locale }: { locale: string }) {
  return (
    <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
      {weekdayLabels(locale).map((name) => (
        <div
          key={name}
          className="truncate px-1 py-1.5 text-center text-xs font-medium capitalize text-gray-500"
        >
          {name}
        </div>
      ))}
    </div>
  );
}

/**
 * The monthly calendar grid.
 *
 * A month has to look like a month, so both layouts keep the seven columns. On
 * a desktop a cell is wide enough for chips; on a phone it is ~50px, which fits
 * a day number and little else — there the cell shows colored dots and tapping
 * it opens that day's full agenda underneath the grid.
 */
export function MonthGrid(props: MonthGridProps) {
  const { isMobile } = useSidebar();
  return isMobile ? <MonthDots {...props} /> : <MonthChips {...props} />;
}

/**
 * Phone layout: day number + one dot per item (violet for reminders, the
 * status color for tasks), with the selected day's agenda below the grid.
 */
function MonthDots({
  weeks,
  tasksByDay,
  remindersByDay,
  meetingsByDay,
  eventsByDay,
  leaveByDay,
  showAssignee,
}: MonthGridProps) {
  const { t, locale } = useI18n();
  const todayKey = toDateKey(new Date());
  const [selected, setSelected] = useState<string | null>(todayKey);

  const days = useMemo(() => weeks.flat(), [weeks]);
  // Paging to another month drops the selection rather than showing a panel for
  // a day that is no longer on screen. Derived, so no effect has to fire.
  const activeDay = days.find((d) => d.key === selected) ?? null;

  const dayFmt = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  const activeTasks = activeDay ? (tasksByDay.get(activeDay.key) ?? []) : [];
  const activeReminders = activeDay ? (remindersByDay?.get(activeDay.key) ?? []) : [];
  const activeMeetings = activeDay ? (meetingsByDay?.get(activeDay.key) ?? []) : [];
  const activeEvents = activeDay ? (eventsByDay?.get(activeDay.key) ?? []) : [];
  const activeLeave = activeDay ? (leaveByDay?.get(activeDay.key) ?? []) : [];

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <WeekdayHeader locale={locale} />
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 border-b border-gray-100 last:border-b-0">
            {week.map((day) => {
              const tasks = tasksByDay.get(day.key) ?? [];
              const reminders = remindersByDay?.get(day.key) ?? [];
              const meetings = meetingsByDay?.get(day.key) ?? [];
              const events = eventsByDay?.get(day.key) ?? [];
              const leave = leaveByDay?.get(day.key) ?? [];
              // Leave leads, then reminders and the other timed items, in the
              // same order the day panel below stacks them. A dot for an
              // absence is what makes a holiday visible from the grid itself,
              // without tapping into every day of the week.
              const colors: (string | null)[] = [
                ...leave.map((entry) => LEAVE_TYPE_COLOR[entry.type]),
                ...reminders.map(() => null),
                ...meetings.map(() => null),
                ...events.map((event) => calendarEventColor(event)),
                ...tasks.map((task) => task.statusColor),
              ];
              const extra = colors.length - MAX_DOTS;
              const isSelected = day.key === selected;
              return (
                <button
                  key={day.key}
                  type="button"
                  onClick={() => setSelected(isSelected ? null : day.key)}
                  aria-pressed={isSelected}
                  aria-label={dayFmt.format(day.date)}
                  className={`flex min-h-14 flex-col items-center gap-1 border-r border-gray-100 py-1.5 last:border-r-0 ${
                    isSelected
                      ? 'bg-brand-100 ring-1 ring-inset ring-brand-600'
                      : day.inMonth
                        ? ''
                        : 'bg-gray-50'
                  }`}
                >
                  <span
                    className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                      day.key === todayKey
                        ? 'bg-brand-600 font-semibold text-white'
                        : day.inMonth
                          ? 'text-gray-700'
                          : 'text-gray-400'
                    }`}
                  >
                    {day.date.getDate()}
                  </span>
                  <span className="flex h-2 items-center gap-0.5">
                    {colors.slice(0, MAX_DOTS).map((color, i) => (
                      <span
                        key={i}
                        className={`h-1.5 w-1.5 rounded-full ${color ? '' : 'bg-violet-500'}`}
                        style={color ? { backgroundColor: color } : undefined}
                      />
                    ))}
                    {extra > 0 && (
                      <span className="text-[9px] leading-none text-gray-400">+{extra}</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {activeDay && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="flex items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-2 py-1.5">
            <span className="truncate text-sm font-medium capitalize text-gray-900">
              {dayFmt.format(activeDay.date)}
            </span>
            <button
              type="button"
              onClick={() => setSelected(null)}
              aria-label={t('calendar.closeDay')}
              className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          </div>
          <div className="py-1">
            {/* Leave first: being away frames the day the rest of it sits in. */}
            {activeLeave.length > 0 && (
              <div className="space-y-1 px-2 pb-1 pt-1">
                {activeLeave.map((entry) => (
                  <LeaveChip
                    key={entry.requestId}
                    leave={entry}
                    locale={locale}
                    showAssignee={showAssignee}
                  />
                ))}
              </div>
            )}
            {activeReminders.length > 0 && (
              <div className="space-y-1 px-2 pb-1 pt-1">
                {activeReminders.map((reminder) => (
                  <ReminderChip key={reminder.id} reminder={reminder} locale={locale} />
                ))}
              </div>
            )}
            {activeMeetings.map((meeting) => (
              <MeetingChip key={meeting.id} meeting={meeting} locale={locale} />
            ))}
            {activeEvents.length > 0 && (
              <div className="space-y-1 px-2 pb-1 pt-1">
                {activeEvents.map((event) => (
                  <CalendarEventChip key={event.id} event={event} locale={locale} />
                ))}
              </div>
            )}
            {/* The shared task row, as in the week agenda. */}
            <ul>
              {activeTasks.map((task) => (
                <TaskListRow
                  key={task.id}
                  task={calendarTaskRow(task)}
                  hideAssignee={!showAssignee}
                />
              ))}
            </ul>
            {/* A day whose only entry is a leave band is not an empty day. */}
            {activeTasks.length === 0 &&
              activeReminders.length === 0 &&
              activeMeetings.length === 0 &&
              activeEvents.length === 0 &&
              activeLeave.length === 0 && (
                <p className="px-3 py-1.5 text-xs font-medium text-gray-500">
                  {t('calendar.weekEmpty')}
                </p>
              )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Desktop layout: each cell shows up to 3 task chips colored by the task's
 * configurable status (hex from the DB → inline style, Tailwind classes would
 * be purged); overflow expands in place. Clicking a chip opens the task's
 * detail page via its human identifier; hovering one shows a rich preview.
 */
function MonthChips({
  weeks,
  tasksByDay,
  remindersByDay,
  meetingsByDay,
  eventsByDay,
  leaveByDay,
  showAssignee,
}: MonthGridProps) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const moveTask = useMoveTaskToDay();

  const todayKey = toDateKey(new Date());

  function toggleExpanded(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function openTask(task: Task) {
    navigate(`/tasks/${encodeURIComponent(task.identifier ?? task.id)}`);
  }

  function renderTaskChip(task: Task) {
    return (
      <TaskHoverCard task={task}>
        <button
          type="button"
          onClick={() => openTask(task)}
          className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-xs font-medium hover:opacity-80"
          style={statusBadgeStyle(task.statusColor)}
        >
          <span className="min-w-0 flex-1 truncate">{task.title}</span>
          {/* One line per chip here, so the avatar alone stands in
              for the name — it carries the name as its tooltip and
              the hover card spells it out. */}
          {showAssignee && task.assignee && <Avatar name={task.assignee.name} size={14} />}
        </button>
      </TaskHoverCard>
    );
  }

  return (
    <CalendarDnd<Task> onMove={moveTask} renderOverlay={renderTaskChip}>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <WeekdayHeader locale={locale} />
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 border-b border-gray-100 last:border-b-0">
            {week.map((day) => {
              const tasks = tasksByDay.get(day.key) ?? [];
              const reminders = remindersByDay?.get(day.key) ?? [];
              const meetings = meetingsByDay?.get(day.key) ?? [];
              const events = eventsByDay?.get(day.key) ?? [];
              const leave = leaveByDay?.get(day.key) ?? [];
              const isToday = day.key === todayKey;
              const isOpen = expanded.has(day.key);
              const visible = isOpen ? tasks : tasks.slice(0, MAX_VISIBLE);
              const hidden = tasks.length - MAX_VISIBLE;
              return (
                <DroppableCalendarDay
                  key={day.key}
                  dayKey={day.key}
                  className={`min-h-24 border-r border-gray-100 p-1.5 align-top last:border-r-0 ${
                    day.inMonth ? '' : 'bg-gray-50'
                  }`}
                >
                  <span
                    className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                      isToday
                        ? 'bg-brand-600 font-semibold text-white'
                        : day.inMonth
                          ? 'text-gray-700'
                          : 'text-gray-400'
                    }`}
                  >
                    {day.date.getDate()}
                  </span>
                  <div className="mt-1 space-y-0.5">
                    {/* Leave first — being away is the frame the rest of the day
                      sits in. Then reminders (Andrei, 2026-09-30), meetings and
                      calendar events: they carry a time of day, tasks only a
                      date, so the timed items read like an agenda. */}
                    {leave.map((entry) => (
                      <LeaveChip
                        key={entry.requestId}
                        leave={entry}
                        locale={locale}
                        showAssignee={showAssignee}
                      />
                    ))}
                    {reminders.map((reminder) => (
                      <ReminderChip key={reminder.id} reminder={reminder} locale={locale} />
                    ))}
                    {meetings.map((meeting) => (
                      <MeetingChip key={meeting.id} meeting={meeting} locale={locale} />
                    ))}
                    {events.map((event) => (
                      <CalendarEventChip key={event.id} event={event} locale={locale} />
                    ))}
                    {visible.map((task) => (
                      <DraggableCalendarItem
                        key={task.id}
                        id={task.id}
                        item={task}
                        dayKey={day.key}
                      >
                        {renderTaskChip(task)}
                      </DraggableCalendarItem>
                    ))}
                    {!isOpen && hidden > 0 && (
                      <button
                        type="button"
                        onClick={() => toggleExpanded(day.key)}
                        className="block w-full rounded px-1 py-0.5 text-left text-xs text-gray-500 hover:bg-gray-100"
                      >
                        {t('calendar.more', { count: hidden })}
                      </button>
                    )}
                  </div>
                </DroppableCalendarDay>
              );
            })}
          </div>
        ))}
      </div>
    </CalendarDnd>
  );
}
